import { AwaitQueue } from 'awaitqueue';
import { clone, sortCompare } from './utils';

import * as roomActions from './actions/roomActions';
import * as peerActions from './actions/peerActions';

import Logger from './Logger';

const logger = new Logger('Spotlight');
/**
 * 布局数据填充, 即完成  layoutConfig 对象数据的填充过程
 */

export default class Spotlights {
	/**
	 * 说明变量定义中带 id 结尾的如无特殊说明,指peerId
	 * @param {*} roomClient
	*/
	constructor(store, roomClient) {

		this._store = store;
		this._layoutConfig = store.getState().room.layoutConfig;
		this._roomClient = roomClient;
		this._queue = new AwaitQueue();

		this._shareId = null;             // 远程共享终端
		this._prevFocusId = null;         // 历史焦点终端
		this._focusId = null;             // 焦点终端
		this._activeId = null;            // 语音激励终端
		this._prevActiveId = null;        // 焦点与语音激励为同一终端时
		this._localId = null;             // 本地终端
		this._historyActiveId = null;     // 语音激励历史成员

		this._allLitePeers = [];          // 所有远程终端peerId列表 {id, videoMuted, joinMode}
		this._pollPeerIds = [];           // 轮询队列

		this._interval = window.config.pollInterval ? window.config.pollInterval : 25000;
		this._pollTimer = null;
	}

	get layoutConfig() {
		return this._layoutConfig;
	}
	set layoutConfig(value) {
		this._layoutConfig = value
	}

	get pollTimer() {
		return this._pollTimer;
	}
	set pollTimer(value) {
		this._pollTimer = value
	}

	get store() {
		return this._store;
	}

	get allLitePeers() {
		return this._allLitePeers;
	}

	get prevActiveId() {
		return this._prevActiveId;
	}
	set prevActiveId(value) {
		this._prevActiveId = value
	}

	/**
	 *
	 * @param {布局配置} layoutConfig
	 * @param {布局类型} mode
	 * @param {是否需要强制执行 _layoutConfigUpdated } force
	 */
	async initLayoutConfig(layoutConfig, mode, force = false) {

		this._queue.push(async () => {

			logger.debug('initLayoutConfig() ');

			if (mode) {

				this._store.dispatch(roomActions.setDisplayMode(mode));
			}

			if (layoutConfig) {

				logger.debug('更新 state 中 layoutConfig 来自 initLayoutConfig 方法');
				this._store.dispatch(roomActions.setLayoutConfig(layoutConfig));
				this._layoutConfig = layoutConfig;
			}

			if (this.allLitePeers.length > 0 || force) {

				await this._layoutConfigUpdated({ source: 'initLayoutConfig', force });
			}

		}).catch((error) => {

			logger.error("initLayoutConfig 错误: %o", error);
		});
	}
	/**
	 * 批量将入会终端都加入到列表中
	 * @param {终端列表} peers
	 */
	async addPeers(peers) {

		this._queue.push(async () => {

			logger.debug('addPeers() [peers:"%o"]', peers);
			for (const peer of peers) {

				if (this._allLitePeers.findIndex((v) => v.id === peer.id) === -1) {

					logger.debug('adding peer [peerId: %s, joinMode: %s]', peer.id, peer.joinMode);

					if (this._prevFocusId === peer.id) {

						this._prevFocusId = null;
						this._focusId = peer.id;
					}
					this._allLitePeers.push({

						id           : peer.id,
						videoMuted   : peer.videoMuted,
						displayName  : peer.displayName,
						joinMode     : peer.joinMode
					});
				}
			}

			if (this._allLitePeers.length > 0) {

				//const _forceMode = this._store.getState().room.forceMode;
				const {
					forceMode: _forceMode,
					mode: _mode
				} = this._store.getState().room;

				const _unLockLayoutConfig = (!_forceMode || (_forceMode && _mode === 'autoSplit'));
				if (_unLockLayoutConfig) {

					await this._layoutConfigUpdated({ source: 'addPeers' });
				}
			}
		}).catch((error) => {

			logger.error("addPeers 错误: %o", error);
		});
	}
	/**
	 * 判断当前终端是否在layoutConfig中
	 * @param {终端id} peerId
	 */
	peerInSpotlights(peerId) {

		return this._layoutConfig.data.find((v) => v.peerId === peerId);
	}
	/**
	 * 更新 this._allLitePeers
	 * @param {*} peerId
	 * @param {*} videoMuted
	 */
	async updatePeerVideoMute(peerId, videoMuted, source) {

		logger.debug('updatePeerVideoMute() [peerId:"%s" videoMuted:"%s" source:"%s"]', peerId, videoMuted, source);

		this._store.dispatch(peerActions.setPeerVideoMute(peerId, videoMuted));
		this._store.dispatch(peerActions.setPeerIsCameraOn(peerId, !videoMuted));

		const _peer = this._allLitePeers.find((v) => v.id === peerId);

		if (_peer) {
			
			_peer.videoMuted = videoMuted;

			const _filterVideo = this._store.getState().room.filterVideo;
			if (_filterVideo && source !== 'consumerLayersChanged') {

				await this._layoutConfigUpdated({ source: 'updatePeerVideoMute' });
			}
		}
	}

	/**
	 * 清理数据
	 */
	clearSpotlights() {

		this._shareId = null;
		this._prevFocusId = null;
		this._focusId = null;
		this._activeId = null;
		this._prevActiveId = null;
		this._localId = null;
		this._historyActiveId = null;
		this._allLitePeers = [];
		this._pollPeerIds = [];

		if (this._queue) {
			this._queue.close();
		}
		this._queue = null;

		this._pollTimer = null;
	}

	/**
	 * 新入会终端加入
	 * @param {终端id} id
	 */
	async newPeer(id, videoMuted, displayName, joinMode) {

		this._queue.push(async () => {

			logger.debug('room "newpeer" event [id: %s, videoMuted: %s, displayName: %s, joinMode: %s]', id, videoMuted,  displayName, joinMode);

			if (this._allLitePeers.findIndex((v) => v.id === id) === -1) // We don't have this peer in the list
			{
				logger.debug('newPeer() | adding peer [peerId: %s, joinMode: %s]', id, joinMode);

				this._allLitePeers.push({

					id,
					videoMuted,
					displayName,
					joinMode
				});

				const _forceMode = this._store.getState().room.forceMode;
				const _unLockLayoutConfig = (!_forceMode || (_forceMode && this._store.getState().room.mode === 'autoSplit'));
				if (_unLockLayoutConfig) {

					if (this._prevFocusId === id) {

						this._prevFocusId = null;
						await this.handleFocus(id);
					} else {

						// 规避轮询时视频窗口被重置
						if (!(this._layoutConfig.data.find((item) => item.type === 'poll') && this._layoutConfig.data.length < this._allLitePeers.length)) {

							await this._layoutConfigUpdated({ source: 'newPeer' });
						}
					}
				}
			}
		}).catch((error) => {

			logger.error("newPeer 错误: %o", error);
		});
	}

	/**
	 * 终端退出
	 * @param {*} id
	 */
	async closePeer(id) {

		this._queue.push(async () => {

			logger.debug('room "peerClosed" event [peerId:%o]', id);

			let _needUpdated = false;

			if (this._shareId === id) {

				this._shareId = null;
				_needUpdated = true;
			}
			if (this._focusId === id) {

				this._focusId = null;
				_needUpdated = true;
			}
			if (this._prevFocusId === id) this._prevFocusId = null;

			if (this._activeId === id) {

				this._activeId = null;
				_needUpdated = true;
			}

			if (this._localId === id) this._localId = null;
			if (this._historyActiveId === id) this._historyActiveId = null;

			this._allLitePeers = this._allLitePeers.filter((peer) => peer.id !== id);
			this._pollPeerIds = this._pollPeerIds.filter((peer) => peer !== id);

			// 如果关闭的终端为 static 则设置为自动模式
			const _layoutConfig = clone(this._layoutConfig);
			const _viewArea = _layoutConfig.data.find((v) => v.peerId === id);
			if (_viewArea) {

				if (_viewArea.type === 'poll') {

					_viewArea.videoId = null;
					_viewArea.peerId = null;
				} else  if (_viewArea.type === 'speech') {

					_viewArea.videoId = null;
					_viewArea.local = null;
					_viewArea.peerId = null;
					_viewArea.source = null;

				} else {

					_viewArea.type = 'auto';
					_viewArea.local = null;
					_viewArea.videoId = null;
					_viewArea.peerId = null;
					_viewArea.displayName = 'auto';
					_viewArea.source = null;
				}
				logger.debug('更新 state 中 layoutConfig 来自 closePeer 方法');
				this._store.dispatch(roomActions.setLayoutConfig(_layoutConfig));
				this.layoutConfig = _layoutConfig;
			}

			//const _forceMode = this._store.getState().room.forceMode;
			//const _unLockLayoutConfig = (!_forceMode || (_forceMode && this._store.getState().room.mode === 'autoSplit'));
			if (true) {

				// 规避轮询时视频窗口被重置
				if (!(this._layoutConfig.data.find((item) => item.type === 'poll') && this._layoutConfig.data.length < this._allLitePeers.length) || _needUpdated) {

					await this._layoutConfigUpdated({ source: 'closePeer' });
				}
			}

		}).catch((error) => {

			logger.error("closePeer 错误: %o", error);
		});
	}

	/**
	 * 主席模式下的布局变更
	 * @param {布局} layoutMode
	 * @param {分屏布局信息} screenPaneList
	 * @param {轮询表} confPollList
	 */
	async handleLayoutChange(layoutMode, screenPaneList, confPollList) {

		this._queue.push(async () => {
			
			logger.debug('handleLayoutChange() [layoutMode: %s] - 开始处理', layoutMode);

			/*
			分屏布局模式，
			0-自动、1-单屏、
			2-2分屏、3-3分屏、4-4分屏、
			5-5分屏、6-6分屏、7-7分屏、8-8分屏、9-9分屏、10-10分屏、11-13分屏、12-16分屏、
			13-录制屏、14-20分屏、15-25分屏、16-演讲者4+1布局、
			17-画中画小图像在右下PIP_RightDown、
			18-30分屏
			*/
			const _layoutModes = {
				0: 'autoSplit',
				1: 'oneSplit',
				2: 'twoSplit',
				3: 'threeSplit',
				4: 'fourSplit',
				5: 'fiveSplit',
				6: 'sixSplit',
				7: 'sevenSplit',
				8: 'eightSplit',
				9: 'nineSplit',
				10: 'tenSplit',
				12: 'sixteenSplit',
				16: 'speaker'
			};

			const _startTime = Date.now();
			const _mode = _layoutModes[layoutMode];

			// 两者不能重复
			if (this._activeId && this._activeId === this._historyActiveId) {

				this._historyActiveId = null;
			}

			if (_mode) {

				const _filterVideo = this._store.getState().room.filterVideo;
				// 初始化 layoutConfig 变量
				const _layoutConfig = clone(this._roomClient.viewsLayout[_mode]);

				logger.debug('handleLayoutChange() [mode:"%s", confPollList:%s, screenPaneList:%s, layoutConfig:%s]', _mode, JSON.stringify(confPollList), JSON.stringify(screenPaneList), JSON.stringify(_layoutConfig));

				if (Array.isArray(screenPaneList)) {

					const _allPeerIds = this._allLitePeers.filter((v) => {
						if (_filterVideo) {

							return v.joinMode === 0 && !v.videoMuted;
						}
						else {

							return v.joinMode === 0;
						}
					}).map((v) => v.id);

					// 对 screenPaneList列表中类型为 opType = 3的 peerId 重置为空
					const _screenPanelPeerIds = [];

					for (let i = 0; i < screenPaneList.length; i++) {

						if ([2, 3].includes(screenPaneList[i].opType)) {

							screenPaneList[i].peerId = '';
						}

						if (screenPaneList[i].peerId) {
							
							if (_screenPanelPeerIds.includes(screenPaneList[i].peerId)) {
								
								screenPaneList[i].peerId = '';
							} else {

								_screenPanelPeerIds.push(screenPaneList[i].peerId);
							}
						}
					}
					const _screenPaneList = screenPaneList;


					// "contentType" 分屏中显示内容类型，0:终端; 1:voip位图; 2:空; 3:双流
					// "opType"      分屏中操作类型，1:指定终端; 2:语音激励;3:自动;4:特殊指定终端（对此终端不进行选流，只显示终端名称）;5:特殊自动轮询（对此轮询终端不进行选流，只显示终端名称）; 6:轮询 ;

					const _willFillPeers = _allPeerIds.filter((peerId) => _screenPaneList.findIndex((v) => v.peerId === peerId && [0, 1].includes(v.contentType)) === -1).map((v) => {
						// share(共享)、focus(焦点)、active(语音激励)、local(本地)、historyActive
						const _willFillPeer = { peerId: v, source: 'remote' };
						if (v === this._focusId) {

							_willFillPeer.source = 'focus';
						} else if (v === this._activeId) {

							_willFillPeer.source = 'active';
						} else if (v === this._localId) {

							_willFillPeer.source = 'local';
						} else if (v === this._historyActiveId) {

							_willFillPeer.source = 'historyActive';
						}

						return _willFillPeer;
					});

					if (this._historyActiveId &&
						_willFillPeers.findIndex((v) => v.peerId === this._historyActiveId && v.source === 'historyActive') === -1 &&
						_screenPaneList.findIndex((v) => v.peerId === this._historyActiveId && [0, 1].includes(v.contentType)) === -1)
					{

						if (_filterVideo) {

							if (this._allLitePeers.findIndex((v) => v.id === this._historyActiveId && !v.videoMuted) > -1) {

								_willFillPeers.push({
									peerId : this._historyActiveId,
									source : 'historyActive'
								});
							}
						} else {

							_willFillPeers.push({
								peerId : this._historyActiveId,
								source : 'historyActive'
							});
						}
					}

					if (this._localId &&
						_screenPaneList.findIndex((v) => v.peerId === this._localId && [0, 1].includes(v.contentType)) === -1) {

						let _findPeer = _willFillPeers.find((v) => v.peerId === this._localId);
						if (_findPeer) {

							_findPeer.source = 'local';
						} else {

							if (_filterVideo) {

								if (this._allLitePeers.findIndex((v) => v.id === this._localId && !v.videoMuted) > -1) {

									_willFillPeers.push({

										peerId : this._localId,
										source : 'local'
									});
								}
							} else {

								_willFillPeers.push({

									peerId : this._localId,
									source : 'local'
								});
							}
						}
					}

					if (this._activeId &&
						_screenPaneList.findIndex((v) => v.peerId === this._activeId && [0, 1].includes(v.contentType)) === -1) {

						let _findPeer = _willFillPeers.find((v) => v.peerId === this._activeId);
						if (_findPeer) {

							_findPeer.source = 'active';
						} else {

							if (_filterVideo) {

								if (this._allLitePeers.findIndex((v) => v.id === this._activeId && !v.videoMuted) > -1) {

									_willFillPeers.push({

										peerId : this._activeId,
										source : 'active'
									});
								}
							} else {

								_willFillPeers.push({

									peerId : this._activeId,
									source : 'active'
								});
							}
						}
					}

					if (this._focusId &&
						_screenPaneList.findIndex((v) => v.peerId === this._focusId && [0, 1].includes(v.contentType)) === -1) {

						let _findPeer = _willFillPeers.find((v) => v.peerId === this._focusId);
						if (_findPeer) {

							_findPeer.source = 'focus';
						} else {

							_willFillPeers.push({

								peerId : this._focusId,
								source : 'focus'
							});
						}
					}

					//let _willFillPeersIdx =  _willFillPeers.findIndex((v) => v.peerId === this._shareId && v.source === 'share');
					let _screenPaneListIdx = _screenPaneList.findIndex((v) => v.peerId === this._shareId && v.contentType === 3);
					if (this._shareId &&
						//_willFillPeersIdx === -1 &&
						_screenPaneListIdx === -1)
					{
						_willFillPeers.push({

							peerId : this._shareId,
							source : 'share'
						});
					}

					// 对 _willFillPeers列表中数据重新排序
					if (_willFillPeers.length > 0) {

						if (this._historyActiveId) {

							const _idx = _willFillPeers.findIndex((v) => v.peerId === this._historyActiveId && v.source === 'historyActive');
							if (_idx >= 0) {

								_willFillPeers.splice(_idx, 1);
								_willFillPeers.unshift({

									peerId : this._historyActiveId,
									source : 'historyActive'
								});
							}
						}

						if (this._localId) {

							const _idx = _willFillPeers.findIndex((v) => v.peerId === this._localId && v.source === 'local');
							if (_idx >= 0) {

								_willFillPeers.splice(_idx, 1);
								_willFillPeers.unshift({

									peerId : this._localId,
									source : 'local'
								});
							}
						}

						if (this._activeId) {

							const _idx = _willFillPeers.findIndex((v) => v.peerId === this._activeId && v.source === 'active');
							if (_idx >= 0) {

								_willFillPeers.splice(_idx, 1);
								_willFillPeers.unshift({
									peerId : this._activeId,
									source : 'active'
								});
							}
						}

						if (this._focusId) {

							const _idx = _willFillPeers.findIndex((v) => v.peerId === this._focusId && v.source === 'focus');
							if (_idx >= 0) {

								_willFillPeers.splice(_idx, 1);
								_willFillPeers.unshift({
									peerId : this._focusId,
									source : 'focus'
								});
							}
						}

						if (this._shareId) {

							const _idx = _willFillPeers.findIndex((v) => v.peerId === this._shareId && v.source === 'share');
							if (_idx >= 0) {

								_willFillPeers.splice(_idx, 1);
								_willFillPeers.unshift({
									peerId : this._shareId,
									source : 'share'
								});
							}
						}
					}

					logger.debug('handleLayoutChange() [filterVideo:%s, allLitePeers:%s, willFillPeerIds:%s, _screenPaneList:%s]', _filterVideo, JSON.stringify(this._allLitePeers), JSON.stringify(_willFillPeers), JSON.stringify(_screenPaneList));
					//logger.debug('执行 handleLayoutChange 生成 willFillPeers 时长[ %s 毫秒]', (Date.now() - _startTime));

					for (let i = 0; i < _screenPaneList.length; i++) {
						// type   : static auto poll
						// source : share(共享)、focus(焦点)、active(语音激励)、local(本地)、historyActive(语音激励历史成员)、 other(其它) remote
						// optype      1:指定终端; 2:语音激励; 3:自动; 6:轮询;
						// contenttype 0:终端; 3:双流(共享)
						if (_screenPaneList[i] && _layoutConfig.data[i]) {

							if (_screenPaneList[i].opType === 1 && _screenPaneList[i].contentType === 3) {

								_layoutConfig.data[i].type   = 'static';
								_layoutConfig.data[i].source = 'share';
								_layoutConfig.data[i].local  = false;

							}
							else if (_screenPaneList[i].opType === 1) {

								_layoutConfig.data[i].type   = 'static';
								_layoutConfig.data[i].source = 'remote';
								_layoutConfig.data[i].local  = false;

								if (_screenPaneList[i].peerId) {
									// 判断这个指定是否同时为语音激励
									const _speechViewArea = this._layoutConfig.data.find((v) => v.type === 'speech' && v.peerId === _screenPaneList[i].peerId);
									if (_speechViewArea) {

										_speechViewArea.local = false;
										_speechViewArea.peerId = '';
										_speechViewArea.source = 'remote';
									}
								}
							}
							else if (_screenPaneList[i].opType === 3) {

								_layoutConfig.data[i].type   = 'auto';
								_layoutConfig.data[i].source = 'remote';
								_layoutConfig.data[i].local  = false;

							}
							else if (_screenPaneList[i].opType === 2) {

								_layoutConfig.data[i].type   = 'speech';
								_layoutConfig.data[i].displayName = 'speech';
								_layoutConfig.data[i].source = 'active';
								_layoutConfig.data[i].local  = false;

							}
							else if (_screenPaneList[i].opType === 6) {

								_layoutConfig.data[i].type = 'poll';
							}

							if (_screenPaneList[i].peerId) {

								_layoutConfig.data[i].peerId = _screenPaneList[i].peerId;

								if (_screenPaneList[i].peerId === this._focusId && _screenPaneList[i].contentType !== 3) {

									_layoutConfig.data[i].source = 'focus';
								} else if (_screenPaneList[i].peerId === this._localId) {

									if (_screenPaneList[i].contentType === 3) {

										_layoutConfig.data[i].type    = 'auto';
										_layoutConfig.data[i].source  = 'remote';
										_layoutConfig.data[i].local   = false;
										_layoutConfig.data[i].peerId  = null;
										_layoutConfig.data[i].videoId = null;
									} else {

										_layoutConfig.data[i].source = 'local';
										_layoutConfig.data[i].local  = true;
									}
								}
							} else if (![6, 2].includes(_screenPaneList[i].opType)) {  // 排除轮询、语音激励窗口

								_layoutConfig.data[i].type    = 'auto';
								_layoutConfig.data[i].source  = 'remote';
								_layoutConfig.data[i].local   = false;
								_layoutConfig.data[i].peerId  = null;
								_layoutConfig.data[i].videoId = null;
							}
						}
					}

					logger.debug('基于远端数据 (screenPaneList) 初始化 _layoutConfig 结果', JSON.stringify(_layoutConfig));

					// 如果有语音激励窗口,则需要填充
					const _speechViewArea = _layoutConfig.data.find((v) => v.type === 'speech');
					if (_speechViewArea) {

						if (this._activeId) {

							const _staticViewArea = _layoutConfig.data.find((v) => v.type === 'static' && v.peerId === this._activeId);

							if (!_staticViewArea) {

								_speechViewArea.peerId = this._activeId;
								_speechViewArea.local  = this._activeId === this._localId ? true : false;
	
								const _idx = _willFillPeers.findIndex((v) => v.peerId === this._activeId && v.source === 'active');
								if (_idx >= 0) {
	
									_willFillPeers.splice(_idx, 1);
								}
							}
						} else if (this._historyActiveId) {

							const _staticViewArea = _layoutConfig.data.find((v) => v.type === 'static' && v.peerId === this._historyActiveId);

							if (!_staticViewArea) {
								
								_speechViewArea.peerId = this._historyActiveId;
								_speechViewArea.local  = this._historyActiveId === this._localId ? true : false;
	
								const _idx = _willFillPeers.findIndex((v) => v.peerId === this._historyActiveId && !v.source === 'share');
								if (_idx >= 0) {
	
									_willFillPeers.splice(_idx, 1);
								}
							}
						}
					}

					logger.debug(`填充语音激励窗口后 _layoutConfig: ${JSON.stringify(_layoutConfig)} _willFillPeers: ${JSON.stringify(_willFillPeers)}`);

					// 主席模式下终端的显示排序远端决定
					// _willFillPeers.sort(objSortCompare('peerId'));
					
					// 是需要独占还是填充其它终端
					const _staticSpeechView = window.config.staticSpeechView;

					for (let i = 0; i < _layoutConfig.data.length; i++) {

						if (_willFillPeers.length > 0 && !_layoutConfig.data[i].peerId) {

							if (_layoutConfig.data[i].type === 'auto') {

								const _willFillPeer = _willFillPeers.shift();
								if (_willFillPeer) {
	
									_layoutConfig.data[i].type    = 'auto';
									_layoutConfig.data[i].peerId  = _willFillPeer.peerId;
									_layoutConfig.data[i].videoId = null;
									_layoutConfig.data[i].local   = _willFillPeer.peerId === this._localId ? true : false;
									_layoutConfig.data[i].source  = _willFillPeer.source;
	
								}
	
							} else if (_layoutConfig.data[i].type === 'speech') {

								if (!_staticSpeechView) {

									const _willFillPeer = _willFillPeers.shift();
									if (_willFillPeer) {

										_layoutConfig.data[i].peerId  = _willFillPeer.peerId;
										_layoutConfig.data[i].local   = _willFillPeer.peerId === this._localId ? true : false;
										_layoutConfig.data[i].source  = _willFillPeer.source;
									}
								}
							}
						}
					}

					// 对轮询单独处理
					const _pollViewAreas = _layoutConfig.data.filter((v) => v.type === 'poll');
					if (_pollViewAreas.length > 0) {

						const _filledPeerIds = _layoutConfig.data.filter((v) => v.peerId).map((v)=> v.peerId);

						const _willPollIds1 = Array.isArray(confPollList) ? confPollList.filter((v) => _allPeerIds.includes(v.peerId) && !_filledPeerIds.includes(v.peerId)).map((v) => v.peerId) : [];
						const _willPollIds2 = _willFillPeers.map((v) => v.peerId).filter(v => !_willPollIds1.includes(v));

						const _willPollIds = _willPollIds1.concat(_willPollIds2);

						logger.debug('主席模式轮询填充[_willPollIds1:%s; _willPollIds2:%s; _willPollIds:%s]', JSON.stringify(_willPollIds1), JSON.stringify(_willPollIds2), JSON.stringify(_willPollIds));

						// 排序 _willPollIds
						//_willPollIds.sort(sortCompare);

						for (const _viewArea of _pollViewAreas) {

							const _pollPeerId = _willPollIds.shift();

							if (_pollPeerId) {

								_viewArea.peerId = _pollPeerId;
								//logger.debug('主席模式轮询填充', _pollPeerId, _willPollIds, _willPollIds1, _willPollIds2);
								if (_pollPeerId === this._localId) {

									_viewArea.local  = true;
									_viewArea.source = 'local';
								} else {

									_viewArea.local  = false;
									_viewArea.source = 'remote';
								}
							} else {
								break;
							}
						}
					}

					logger.debug('叠加本地及轮询数据初始化 _layoutConfig 结果', JSON.stringify(_layoutConfig));
					//logger.debug('执行 handleLayoutChange  完成 layoutConfig 时长 [ %s 毫秒]',(Date.now() - _startTime));

					let _isEqual = false;

					if (!this._simpleObjEqual(this._layoutConfig, _layoutConfig)) {

						logger.debug('handleLayoutChange() | spotlights updated, emitting [layoutConfig: "%s"]', JSON.stringify(_layoutConfig));

						// 比较 _layoutConfig.data.peerId 是否相等
						const _prevPeerIds = this._layoutConfig.data.filter((v) => v.peerId).map((v) => ({
							id     : v.id,
							peerId : v.peerId,
							source : v.source,
							type   : v.type
						}));

						const _peerIds = _layoutConfig.data.filter((v) => v.peerId).map((v) => ({
							id     : v.id,
							peerId : v.peerId,
							source : v.source,
							type   : v.type
						}));

						_isEqual = this._arrEqual(_prevPeerIds, _peerIds, false);

						if (!this._arrEqual(_prevPeerIds, _peerIds)) {

							await this._roomClient.updateConsumers(_layoutConfig);  // 要求服务端创建需要显示的consumer 及 取消consumer 暂停
						}

					} else {
						
						logger.debug('handleLayoutChange() | spotlights not updated');
					}

					if (_filterVideo) {

						const _viewArea = _layoutConfig.data.find((v) => v.peerId);
						if (!_viewArea && _layoutConfig.data.length > 0) {

							Object.assign(_layoutConfig.data[0], {

								videoId     : null,
								local       : true,
								peerId      : this._localId,
								displayName : 'auto',
								source      : 'local',
								type        : 'auto'
							});
						}
					}

					//const _prevMode = this._store.getState().room.mode;
					const _prevMode = this._layoutConfig.name;
					logger.debug(`布局: [_prevMode:${_prevMode}; currMode:${_mode}]`);

					if (_prevMode === _mode && _isEqual) {

						logger.debug('处理等大窗口', JSON.stringify(this._layoutConfig), JSON.stringify(_layoutConfig));
						// 处理等大窗口
						let _shouldUpdate = false;

						const _focusView = _layoutConfig.data.find((v) => v.source === 'focus');
						if (_focusView) {

							const _videoView = this._layoutConfig.data.find((v) => v.peerId === _focusView.peerId);
							if (_videoView && _focusView.size !== _videoView.size) {

								_shouldUpdate = true;
							}
						}

						if (!_shouldUpdate) {

							const _activeView = _layoutConfig.data.find((v) => v.source === 'active');
							if (_activeView) {

								const _videoView = this._layoutConfig.data.find((v) => v.peerId === _activeView.peerId);
								if (_videoView && _activeView.size !== _videoView.size) {

									_shouldUpdate = true;
								}
							}
						}

						if (!_shouldUpdate) {
							
							const _firstBlankAreaIdx = this._layoutConfig.data.findIndex((v) => v.peerId === null);

							if (_firstBlankAreaIdx > -1) {
	
								const _areaViews = this._layoutConfig.data.slice(_firstBlankAreaIdx);
								const _firstFillAreaIdx = _areaViews.findIndex((v) => v.peerId !== null);
	
								if (_firstFillAreaIdx >= 0) {
	
									_shouldUpdate = true;
								}
							}	
						}

						if (!_shouldUpdate) {

							const _hasSpeechViewArea = _layoutConfig.data.find((v) => v.type === 'speech');
							if (_hasSpeechViewArea) {
								
								_shouldUpdate = true;
							}	
						}

						if (_shouldUpdate) {

							this._store.dispatch(roomActions.setLayoutConfig(_layoutConfig));
							this._layoutConfig = _layoutConfig;
						} else {

							logger.debug(`主席模式:${_mode} | spotlights not updated`);
						}
					} else {

						if (_prevMode !== _mode) {

							this._store.dispatch(roomActions.setDisplayMode(_mode));
						}
						logger.debug('更新 state 中 layoutConfig 来自 handleLayoutChange 方法', JSON.stringify(_layoutConfig));
						//logger.debug('执行 handleLayoutChange 调用 updateConsumers 时长 [ %s 毫秒]', (Date.now() - _startTime));
						this._store.dispatch(roomActions.setLayoutConfig(_layoutConfig));
						this._layoutConfig = _layoutConfig;
					}

				} else {
					// 主席模式下的自动分屏
					await this._layoutConfigUpdated({
						source       : 'handleLayoutChange',
						layoutConfig : _layoutConfig
					});
				}
			} else {

				// 如果是不支持的分屏,则选默认演讲者布局
				await this._layoutConfigUpdated({
					source: `handleLayoutChange  不支持分屏[ ${layoutMode} ] 选择默认 speaker`
				});
			}

			const _endTime = Date.now();
			logger.debug('执行 handleLayoutChange 结束 时长 [ %s 毫秒]', (_endTime - _startTime));

			logger.debug('handleLayoutChange() [layoutMode: %s] - 结束处理', layoutMode);

		}).catch((error) => {

			logger.error("handleLayoutChange 错误: %o", error);
		});
	}

	/**
	 * 处理共享/停止共享业务
	 * @param {*} peerId
	 */
	async handleShare(peerId, mode) {

		this._queue.push(async () => {

			logger.debug('handleShare() [peerId:"%s" mode:"%s"]', peerId, mode);

			if (mode) {
				this._store.dispatch(roomActions.setDisplayMode(mode));

				const _layoutConfig = clone(this._roomClient.viewsLayout[mode]);
				this.layoutConfig = _layoutConfig;
			}

			if (this._shareId !== peerId) {

				this._shareId = peerId;
				let _shouldUpdate = true;
				if (!peerId) { // 如果停止了共享,则恢复到历史模式

					const _historyMode = this._store.getState().room.historyMode;
					const _prevMode = _historyMode.pop();

					if (_prevMode && _historyMode.length === 0) {
						
						this._store.dispatch(roomActions.setDisplayMode(_prevMode));

						const _layoutConfig = clone(this._roomClient.viewsLayout[_prevMode]);
						this._layoutConfig = _layoutConfig;
					} else {

						const _viewArea = this._layoutConfig.data.find((v) => v.source === 'share' && v.type === 'static');
						if (_viewArea) {

							_viewArea.type        = 'auto';
							_viewArea.local       = null;
							_viewArea.videoId     = null;
							_viewArea.peerId      = null;
							_viewArea.displayName = 'auto';
							_viewArea.source      = null;
						}
					}
				} else {
					// 查找 peerId 是否在可视化窗口里
					const _videoView = this._layoutConfig.data.find((v) => v.peerId === peerId);

					if (_videoView) {
						switch (this._layoutConfig.name) {
							case 'fiveSplit':
							case 'speaker':
								if ((_videoView.size === 1)) // 如果在大屏则不更新
								{
									_shouldUpdate = false;
								}
								break;
							case 'autoSplit':
							case 'oneSplit':
							case 'twoSplit':
							case 'threeSplit':
							case 'fourSplit':
							case 'nineSplit':
							case 'sixteenSplit': {
								// 一分屏、二分屏、四分屏、九分屏、十六分屏全部为等大窗口只要存在则不更新
								_shouldUpdate = false;
								break;
							}
							case 'sixSplit':
								if ((_videoView.size === 3)) // 如果在大屏则不更新
								{
									_shouldUpdate = false;
								}
								break;
							case 'sevenSplit':
								if (_videoView.size === 4) {
									_shouldUpdate = false
								}
								break;
							case 'tenSplit':
								if (_videoView.size === 4) {
									_shouldUpdate = false
								}
								break;
							default:
							//默认代码块
								break;
						}
					}
				}

                if (_shouldUpdate) {

					await this._layoutConfigUpdated({ source: 'handleShare' });
				}
			}

		}).catch((error) => {

			logger.error("handleShare 错误: %o", error);
		});
	}

	/**
	 * 焦点peerId
	 * @param {*} peerId
	*/
	async handleFocus(peerId) {

		this._queue.push(async () => {

			logger.debug('handleFocus() [peerId:%s]', peerId);

			if ((peerId && this._allLitePeers.findIndex((v) => v.id === peerId) >= 0) || peerId === null) {

				const _staticViewArea = this._layoutConfig.data.find((v) => v.type === 'static' && v.peerId === peerId);
				const _speechViewArea = this._layoutConfig.data.find((v) => v.type === 'speech');
				if (!_staticViewArea && _speechViewArea && peerId && this._activeId === peerId) {

					_speechViewArea.local = peerId === this._localId ? true : false;
					_speechViewArea.peerId = peerId;
					_speechViewArea.source = 'active';

					this._store.dispatch(roomActions.setLayoutConfig(this._layoutConfig));

					await this._layoutConfigUpdated({ source: 'handleFocus' });

				} else {

					if (this._focusId !== peerId) {
						
						this._focusId = peerId;
						if (this._focusId === this._activeId) {
							
							this._activeId = null;
						}
	
						if (this._focusId === this._historyActiveId) {
							
							this._historyActiveId = null;
						}
	
						// 如果取消焦点并且当前会议中有语音激励,则恢复语音激励
						if (this._focusId === null && this._prevActiveId) {
	
							this._activeId = this._prevActiveId;
							this._prevActiveId = null;
						}
	
						const _forceMode = this._store.getState().room.forceMode;
						// const _unLockLayoutConfig = (!_forceMode || (_forceMode && this._store.getState().room.mode === 'autoSplit'));
	
						if (true) {
	
							// 查找 peerId 是否在可视化窗口里
							const _videoView = this._layoutConfig.data.find((v) => v.peerId === peerId && v.source !== 'share');
							let _shouldUpdate = true;
	
							if (_videoView) {
	
								switch (this._layoutConfig.name) {
									case 'fiveSplit':
									case 'speaker': {
										const _staticVideoView = this._layoutConfig.data.find((v) => v.type === 'static' && v.size === 1 && v.peerId);
										if ((_videoView.size === 1) || // 如果在大屏则不更新
											(_videoView.size === 7 && (this._shareId || _staticVideoView))) // 如果在小屏但大屏被共享或指定占用则不更新
										{
											_shouldUpdate = false;
										} else if (_videoView.size === 7 && peerId) {
	
											// 此时判断是否需要与大屏窗口的视频更换位置
											const _largeVideoView = this._layoutConfig.data.find((v) => v.size === 1 && v.type === 'auto' && v.source !== 'share');
											if (_largeVideoView && _videoView.type === 'auto') {
	
												// _largeVideoView 与 _videoView 进行更换
												const _largetVideo = {
	
													local       : _largeVideoView.local,
													peerId      : _largeVideoView.peerId,
													displayName : _largeVideoView.displayName
												};
	
												const _smallVideo = {
	
													local       : _videoView.local,
													peerId      : _videoView.peerId,
													displayName : _videoView.displayName
												}
	
												Object.assign(_largeVideoView, _smallVideo);
												Object.assign(_videoView, _largetVideo);
	
												// 将修改后的结果更新至 store 中
												this._store.dispatch(roomActions.setLayoutConfig(this._layoutConfig));
	
												_shouldUpdate = false;
											}
										}
	
										break;
									}
									case 'autoSplit':
									case 'oneSplit':
									case 'twoSplit':
									case 'threeSplit':
									case 'fourSplit':
									case 'nineSplit':
									case 'sixteenSplit':
										// 一分屏、二分屏、四分屏、九分屏、十六分屏全部为等大窗口只要存在则不更新
										_shouldUpdate = false;
										break;
									case 'sixSplit': {
										const _staticVideoView = this._layoutConfig.data.find((v) => v.type === 'static' && v.size === 3 && v.peerId);
										if ((_videoView.size === 3) || // 如果在大屏则不更新
											(_videoView.size === 5 && (this._shareId || _staticVideoView))) // 如果在小屏但大屏被共享或焦点任意一个占用则不更新
										{
											_shouldUpdate = false;
										}
										break;
									}
									case 'sevenSplit': {
										const _staticVideoViews = this._layoutConfig.data.filter((v) => v.type === 'static' && v.size === 4 && v.peerId);
										if ((_videoView.size === 4) ||
											(_videoView.size === 6 && (_staticVideoViews.length === 3))
											) {
											_shouldUpdate = false
										}
										break;
									}
									case 'tenSplit': {
										const _staticVideoViews = this._layoutConfig.data.filter((v) => v.type === 'static' && v.size === 4 && v.peerId);
										if ((_videoView.size === 4) ||
											(_videoView.size === 6 && (_staticVideoViews.length === 2))
										) {
											_shouldUpdate = false
										}
										break;
									}
									default:
										//默认代码块
										break;
								}
							}
	
							if (_shouldUpdate) {
	
								await this._layoutConfigUpdated({ source: 'handleFocus' });
							}
						}
					}	
				}

			} else {

				logger.debug(`handleFocus() [${peerId}] 不在参会人列表中`);

				this._prevFocusId = peerId;
			}
		}).catch((error) => {

			logger.error("handleFocus 错误: %o", error);
		});
	}

	/**
	 * 语音激励 对 spotlight 里面的数据进行重排
	 * 修改实例 spotlights 中的属性(this._activeId 及 this._layoutConfig)信息
	 * @param {string} peerId
	 */
	async handleActiveSpeaker(peerId) {

		this._queue.push(async () => {

			logger.debug('handleActiveSpeaker() [peerId: %s] - 开始处理', peerId);

			if ((peerId && this._allLitePeers.findIndex((v) => v.id === peerId) >= 0)) {

				if (this._activeId !== peerId) {

					// 对布局中的语音激励窗口数据进行清理
					if (this._focusId === peerId) {
						const _speechViewArea = this._layoutConfig.data.find((v) => v.type === 'speech');
						if (!_speechViewArea) {
							
							return;	
						} else {
							
							this._focusId = null;	
						}
					}

					if (this._activeId === this._historyActiveId) {

						this._historyActiveId = null;
					}

					this._historyActiveId = this._activeId;
					this._activeId = peerId;

					this._pollPeerIds = this._pollPeerIds.filter((peer) => peer !== peerId);  // 从轮询表中踢除

					const { 
						forceMode: _forceMode, 
						mode: _mode 
					} = this._store.getState().room;

					//const _unLockLayoutConfig = (!_forceMode || (_forceMode && ['autoSplit','speaker'].includes(_mode)));
					if (true) {

						const _viewArea = this._layoutConfig.data.find((v) => v.peerId === peerId);
						if (_viewArea && _viewArea.type === 'poll') {

							_viewArea.videoId = null;
							_viewArea.peerId = null;
							logger.debug('更新 state 中 layoutConfig 来自 handleActiveSpeaker 方法');
							this._store.dispatch(roomActions.setLayoutConfig(this._layoutConfig));
						}

						let _shouldUpdate = true;
						let _force = false;

						const _staticViewArea = this._layoutConfig.data.find((v) => v.type === 'static' && v.peerId === peerId);
						const _speechViewArea = this._layoutConfig.data.find((v) => v.type === 'speech');
						if (!_staticViewArea  && _speechViewArea) {

							if (!_forceMode){
								
								_speechViewArea.local = peerId === this._localId ? true : false;
							
								if (_speechViewArea.peerId !== peerId) {
	
									_speechViewArea.peerId = peerId;
								} 
								_force = true;
								
								_speechViewArea.source = 'active';
	
								this._store.dispatch(roomActions.setLayoutConfig(this._layoutConfig));
							}
						} else {
							// 查找 peerId 是否在可视化窗口里
							const _videoView = this._layoutConfig.data.find((v) => v.peerId === peerId);

							logger.debug(`语音激励终端在布局中的位置信息: ${JSON.stringify(_videoView)}`);

							if (_videoView) {
								switch (this._layoutConfig.name) {
									case 'fiveSplit':
									case 'speaker': {
										const _staticVideoView = this._layoutConfig.data.find((v) => v.type === 'static' && v.size === 1 && v.peerId);
										if ((_videoView.size === 1) || // 如果在大屏则不更新
											(_videoView.size === 7 && (this._shareId || this._focusId || _staticVideoView))
										) // 如果在小屏但大屏被共享、焦点、指定任意一个占用则不更新
										{
											_shouldUpdate = false;
										}
										break;
									}
									case 'autoSplit':
									case 'oneSplit':
									case 'twoSplit':
									case 'threeSplit':
									case 'fourSplit':
									case 'nineSplit':
									case 'sixteenSplit': {
										// 一分屏、二分屏、四分屏、九分屏、十六分屏全部为等大窗口只要存在则不更新
										/*
										const _isUpdate = this.swapSpeechViewArea(this._layoutConfig);
										if (_isUpdate) {
											this._store.dispatch(roomActions.setLayoutConfig(this._layoutConfig));
										}
										*/
										_shouldUpdate = false;
										break;
									}
									case 'sixSplit': {
										const _staticVideoView = this._layoutConfig.data.find((v) => v.type === 'static' && v.size === 3 && v.peerId);
										if ((_videoView.size === 3) || // 如果在大屏则不更新
											(_videoView.size === 5 && (this._shareId || this._focusId || _staticVideoView))) // 如果在小屏但大屏被共享或焦点任意一个占用则不更新
										{
											_shouldUpdate = false;
										}
										break;
									}
									case 'sevenSplit':
										if (_videoView.size === 4) {
											_shouldUpdate = false
										}
										break;
									case 'tenSplit': {
										const _staticVideoViews = this._layoutConfig.data.filter((v) => v.type === 'static' && v.size === 4 && v.peerId);
										if (_videoView.size === 4) {

											_shouldUpdate = false
										} else if (_videoView.size === 6 && ((this._shareId && this._focusId) || _staticVideoViews.length === 2)) { // 如果在小屏并且二个大屏都被占用则不更新

											_shouldUpdate = false
										}
										break
									}
									default:
									// 默认代码块
								}
							}
						}

						if (_shouldUpdate) {

							await this._layoutConfigUpdated({ 
								source: 'handleActiveSpeaker', 
								force: _force 
							});
						}
					}
				}
			}

			logger.debug('handleActiveSpeaker() [peerId: %s] - 结束处理', peerId);

		}).catch((error) => {

			logger.error("handleActiveSpeaker 错误: %o", error);
		});
	}

	/**
	 * 本地peerId
	 * @param {*} peerId
	 */
	async handleLocal(peerId, videoMuted) {

		this._queue.push(async () => {

			logger.debug('handleLocal() [peerId:"%s"]', peerId);

			if (this._localId !== peerId) {

				if (this._localId && (this._localId === this._focusId)) {
					// 如果本地的 peerId 已经被设为焦点了，不处理
					return;
				}

				if (this._localId === this._historyActiveId) {
					this._historyActiveId = null;
				}

				this._localId = peerId;
				if (this._allLitePeers.findIndex((v) => v.id === peerId) === -1) // 将自己加入 _allPeerIds 队列
				{
					this._allLitePeers.push({ id: peerId, videoMuted, joinMode: 0 });
				}

				const _forceMode = this._store.getState().room.forceMode;
				const _unLockLayoutConfig = (!_forceMode || (_forceMode && this._store.getState().room.mode === 'autoSplit'));
				if (_unLockLayoutConfig) {

					await this._layoutConfigUpdated({ source: 'handleLocal' });
				}
			}
		}).catch((error) => {

			logger.error("handleLocal 错误: %o", error);
		});
	}

	_simpleObjEqual(obj1, obj2) {

		return JSON.stringify(obj1) === JSON.stringify(obj2)
	}

	_arrEqual(arr1, arr2, compareSource = true) {

		// 如果长度不相等则直接返回 false
		if (arr1.length !== arr2.length) {
			return false;
		}

		// 对数组 arr1 重新排序
		const _arr1 = arr1.sort((a, b) => {
				if (a.peerId < b.peerId) { return -1; }
				if (a.peerId > b.peerId) { return 1; }
				return 0;
			});

		// 对数组 arr2 重新排序
		const _arr2 = arr2.sort((a, b) => {
				if (a.peerId < b.peerId) { return -1; }
				if (a.peerId > b.peerId) { return 1; }
				return 0;
			});

		if (compareSource) {

			for (let i = _arr1.length; i--;) {

				if ((_arr1[i].peerId !== _arr2[i].peerId) || (_arr1[i].source !== _arr2[i].source)) {

					return false;
				}
			}
		} else {

			// 对 type = 'static' 数据进行处理
			const _staticArr1 = _arr1.filter((v) => v.type === 'static');
			const _staticArr2 = _arr2.filter((v) => v.type === 'static');

			if (_staticArr1.length !== _staticArr2.length) {

				return false;
			} else {

				for (let i = _staticArr1.length; i--;) {

					if ((_staticArr1[i].peerId !== _staticArr2[i].peerId) || (_staticArr1[i].id !== _staticArr2[i].id)) {
						return false;
					}
				}
			}

			for (let i = _arr1.length; i--;) {

				if ((_arr1[i].peerId !== _arr2[i].peerId)) {
					return false;
				}
			}
		}

		return true;
	}

	/**
	 * 取出第一个peerId,放到队列后面
	 */
	async handlePoll(layoutConfig, prevPollPeerIds, interval = false) {

		let _layoutConfig = clone(layoutConfig);

		if (this._pollPeerIds.length > 0) {

			const _pollViewAreas = _layoutConfig.data.filter((item) => item.type === 'poll');

			if (_pollViewAreas.length > 0) {

				let _pollStep = _pollViewAreas.length;

				//#region  在这里需要判断 this._pollPeerIds 的前面与 prevPollPeerIds 是否相等，如果相等则需要处理

				// 如果轮询窗口大于等于队列的长度则步长为1,否则步长为实际窗口的数量
				_pollStep = (_pollStep >= this._pollPeerIds.length) ? 1 : _pollStep;
				const _prevPollPeerIds = this._pollPeerIds.slice(0, _pollStep);

				if (prevPollPeerIds.length > 0 && _prevPollPeerIds.toString() === prevPollPeerIds.toString()) {

					logger.debug(`handlePoll() | _prevPollPeerIds ===  prevPollPeerIds`);

					const _prevPollItems = this._pollPeerIds.splice(0, _pollStep);
					this._pollPeerIds = this._pollPeerIds.concat(_prevPollItems);
				}
				//#endregion

				for (let i = 0; i < _pollViewAreas.length; i++) {

					if (this._pollPeerIds[i]) {

						_pollViewAreas[i].videoId = null;
						_pollViewAreas[i].peerId = this._pollPeerIds[i];
						_pollViewAreas[i].displayName = 'poll';

						if (this._pollPeerIds[i] === this._localId) {

							_pollViewAreas[i].local = true;
							_pollViewAreas[i].source = 'local';
						} else {

							_pollViewAreas[i].local = false;
							_pollViewAreas[i].source = 'remote';
						}

					} else {
						break;  // 跳出循环
					}
				}

				if (!this._simpleObjEqual(this._layoutConfig, _layoutConfig) && interval) {

					logger.debug('handlePoll() | spotlights updated, emitting [layoutConfig: "%o"]', _layoutConfig);

					// 比较 _layoutConfig.data.peerId 是否相等
					const _prevPeerIds = this._layoutConfig.data.filter((v) => v.peerId).map((v) => (
						{
							id     : v.id,
							peerId : v.peerId,
							source : v.source,
							type   : v.type
						}));

					const _peerIds = _layoutConfig.data.filter((v) => v.peerId).map((v) => (
						{
							id     : v.id,
							peerId : v.peerId,
							source : v.source,
							type   : v.type
						}));

					if (!this._arrEqual(_prevPeerIds, _peerIds)) {

						//_layoutConfig = await this._roomClient.updateConsumers(_layoutConfig);  // 轮询时更新
						await this._roomClient.updateConsumers(_layoutConfig);  // 轮询时更新
					}
				} else {

					logger.debug('handlePoll() | spotlights not updated');
				}

				// 轮询队列位移
				const _pollQueueLng = this._pollPeerIds.length;
				if (_pollQueueLng > 0) {
					// 如果轮询窗口大于等于队列的长度则步长为1,否则步长为实际窗口的数量
					_pollStep = (_pollStep >= _pollQueueLng) ? 1 : _pollStep;

					const _prevPollItems = this._pollPeerIds.splice(0, _pollStep);
					this._pollPeerIds = this._pollPeerIds.concat(_prevPollItems);

					if (interval) {

						logger.debug('更新 state 中 layoutConfig 来自 handlePoll 方法');
						this._store.dispatch(roomActions.setLayoutConfig(_layoutConfig));
						this._layoutConfig = _layoutConfig;
					}

					logger.debug('轮询执行结束: [配置: %o, 步长: %s, 队列: %o -> %o] 状态: %s', _layoutConfig, _pollStep, _prevPollItems, this._pollPeerIds, interval);
				}
			}
		}

		return _layoutConfig;
	}

	/**
	 * 重新填充属性 layoutConfig中的数据 并基于 layoutConfig 进行视频处理
	 * 在列表中的则请求开启,没在列表中的则暂停视频流
	 * @param {object} param0
	 */
	async _layoutConfigUpdated({ source, layoutConfig, force = false }) {

		logger.debug('_layoutConfigUpdated() [force:"%s" source:"%s"]', force, source);

		const _startTime = Date.now();

		const { 
			filterVideo : _filterVideo, 
			forceMode   : _forceMode 
		} = this._store.getState().room;

		const _prevPollPeerIds = [];

		// 两者不能重复
		if (this._activeId && this._activeId === this._historyActiveId) {

			this._historyActiveId = null;
		}

		// 对象复制
		let _layoutConfig = layoutConfig ? layoutConfig : clone(this._layoutConfig);

		_layoutConfig.data.map((v) => {

			if (['auto', 'speech'].includes(v.type)) {

				if (v.type === 'auto') {
					v.local       = null;
					v.videoId     = null;
					v.peerId      = null;
					v.displayName = v.type;
					v.source      = null;	
				} else if (v.type === 'speech' && v.peerId !== this._activeId ) {

					v.local       = null;
					v.videoId     = null;
					v.peerId      = null;
					v.displayName = v.type;
					v.source      = null;	
				}
			} else if (v.type === 'poll' && v.peerId && !_forceMode) { // 如果为主席模式下的轮询,则不需要清空 poll 中的 peerId

				_prevPollPeerIds.push(v.peerId);

				v.peerId   = null;
			}
			return v;
		});
		logger.debug('layoutConfig 中 [auto] 块重置为初始值完成', JSON.stringify(_layoutConfig.data));

		const _staticPeerIds = _layoutConfig.data.filter((v) => ['static', 'poll'].includes(v.type) && v.source !== 'share').map((v) => v.peerId);

		let _activeIdUsed = false;
		const _speechViewArea = _layoutConfig.data.find((v) => v.type === 'speech');
		if (_speechViewArea && this._activeId && !_staticPeerIds.includes(this._activeId)) {

			_speechViewArea.local  = (this._activeId === this._localId ? true : false);
			_speechViewArea.peerId = this._activeId;
			_speechViewArea.source = 'active';

			_activeIdUsed = true;
		}

		// R 类视频列表
		const autoVideos = [];

		if (this._shareId) {  // 共享视频

			const _hasShareVideo = _layoutConfig.data.find((v) => v.type === 'static' && v.source === 'share' && v.peerId === this._shareId);

			if (!_hasShareVideo) {

				autoVideos.push({
					peerId : this._shareId,
					source : 'share'
				});
			}
		}

		if (this._focusId &&
			!_staticPeerIds.includes(this._focusId)
		) {    // 焦点视频

			autoVideos.push({
				peerId : this._focusId,
				source : 'focus'
			});
		}

		if (this._activeId && !_activeIdUsed &&
			!_staticPeerIds.includes(this._activeId) &&
			autoVideos.findIndex((v) => v.peerId === this._activeId && v.source !== 'share') === -1
		) {    // 激励视频

			if (_filterVideo) {

				if (this._allLitePeers.findIndex((v) => v.id === this._activeId && !v.videoMuted) > -1) {

					autoVideos.push({
						peerId : this._activeId,
						source : 'active'
					});
				}
			}
			else {

				autoVideos.push({
					peerId : this._activeId,
					source : 'active'
				});
			}
		}

		if (this._localId &&
			!_staticPeerIds.includes(this._localId) &&
			autoVideos.findIndex((v) => v.peerId === this._localId) === -1
		) {    // 本地视频

			if (_filterVideo) {

				if (this._allLitePeers.findIndex((v) => v.id === this._localId && !v.videoMuted) > -1) {

					autoVideos.push({
						peerId : this._localId,
						source : 'local'
					});
				}
			}
			else {

				autoVideos.push({
					peerId : this._localId,
					source : 'local'
				});
			}
		}

		if (this._historyActiveId &&
			!_staticPeerIds.includes(this._historyActiveId) &&
			autoVideos.findIndex((v) => v.peerId === this._historyActiveId && v.source !== 'share') === -1
		) {    // 历史激励视频

			if (_filterVideo) {

				if (this._allLitePeers.findIndex((v) => v.id === this._historyActiveId && !v.videoMuted) > -1) {

					autoVideos.push({
						peerId : this._historyActiveId,
						source : 'historyActive'
					});
				}
			}
			else {

				autoVideos.push({
					peerId : this._historyActiveId,
					source : 'historyActive'
				});
			}
		}

		logger.debug('layoutConfig 中待填充的R视频数组',_filterVideo, JSON.stringify(autoVideos));

		// 语音激励窗口是否需要自动填充
		const _staticSpeechView = window.config.staticSpeechView;

		for (const viewArea of _layoutConfig.data) {

			if (['auto'].includes(viewArea.type) || (viewArea.type === 'speech' && !_staticSpeechView && ! viewArea.peerId)) {

				// share(共享)
				const _shareIdx = autoVideos.findIndex((v) => v.peerId && v.source === 'share');
				if (_shareIdx >= 0) {

					//viewArea.local = false;
					viewArea.peerId = this._shareId;
					viewArea.source = 'share';
					autoVideos.splice(_shareIdx, 1);
					continue;
				}
				// focus(焦点)
				const _focusIdx = autoVideos.findIndex((v) => v.peerId && v.source === 'focus');
				if (_focusIdx >= 0) {

					if (this._focusId === this._localId) {
						viewArea.local = true;
					} else {
						viewArea.local = false;
					}

					viewArea.peerId = this._focusId;
					viewArea.source = 'focus';
					autoVideos.splice(_focusIdx, 1);
					continue;
				}
				// active(语音激励)
				const _activeIdx = autoVideos.findIndex((v) => v.peerId && v.source === 'active');
				if (_activeIdx >= 0) {

					viewArea.local = false;
					viewArea.peerId = this._activeId;
					viewArea.source = 'active';
					autoVideos.splice(_activeIdx, 1);
					continue;
				}
				// local(本地)
				const _localIdx = autoVideos.findIndex((v) => v.peerId && v.source === 'local');
				if (_localIdx >= 0) {

					viewArea.local = true;
					viewArea.peerId = this._localId;
					viewArea.source = 'local';
					autoVideos.splice(_localIdx, 1);
					continue;
				}
				// historyActive(语音激励历史成员)
				const _historyActiveIdx = autoVideos.findIndex((v) => v.peerId && v.source === 'historyActive');
				if (_historyActiveIdx >= 0) {

					viewArea.local  = false;
					viewArea.peerId = this._historyActiveId;
					viewArea.source = 'historyActive';
					autoVideos.splice(_historyActiveIdx, 1);
					continue;
				}

			} else {
				continue;
			}
		}

		logger.debug('layoutConfig 中 [auto] 块填充R视频完成', JSON.stringify(_layoutConfig.data.filter((v) => v.peerId)));

		const _allPeerIds = this._allLitePeers.filter((v) => {
			if (_filterVideo) {

				return v.joinMode === 0 && !v.videoMuted
			}
			else {

				return v.joinMode === 0
			}
		}).map((v) => v.id);
		// 排序 _allPeerIds 已经基于状态值对视频窗口进行了过滤处理
		_allPeerIds.sort(sortCompare);

		const _autoViewAreas = _layoutConfig.data.filter((item) => {

			const _viewAreaTypes = ['auto'];
			if (!_staticSpeechView) {

				_viewAreaTypes.push('speech');
			}

			if (_viewAreaTypes.includes(item.type) && !['share', 'focus', 'active', 'local', 'historyActive'].includes(item.source)) {

				return true;
			}
		});

		if (_autoViewAreas.length > 0) {

			// 已经填充的 peerId 列表
			const _filledPeerIds = _layoutConfig.data.filter((v) => v.peerId !== null && v.source !== 'share').map((v) => v.peerId);
			// 未使用 peerId 列表
			const _willFillPeerIds = _allPeerIds.filter((peerId) => !_filledPeerIds.includes(peerId));

			logger.debug(`autoViewAreas :[${JSON.stringify(_autoViewAreas)}]' willFillPeerIds:[${JSON.stringify(_willFillPeerIds)}]`);

			for (let i = 0; i < _autoViewAreas.length; i++) {

				_autoViewAreas[i].videoId     = null;
				_autoViewAreas[i].displayName = _autoViewAreas[i].type;
				if (i < _willFillPeerIds.length) {

					_autoViewAreas[i].local  = false;
					_autoViewAreas[i].peerId = _willFillPeerIds[i];
					_autoViewAreas[i].source = 'remote';
				} else {

					_autoViewAreas[i].local  = null;
					_autoViewAreas[i].peerId = null;
					_autoViewAreas[i].source = null;
				}
			}

			logger.debug('layoutConfig 中 [auto] 块填充其它视频完成', JSON.stringify(_layoutConfig.data.filter((v) => v.peerId)));
		}

		if (_layoutConfig.data.find((item) => item.type === 'poll')) {

			const _filledPeerIds = _layoutConfig.data.filter((v) => v.peerId !== null && ['static', 'auto', 'speech'].includes(v.type)).map((v) => v.peerId);
			// 将共享 peerId 从 _filledPeerIds 中删除
			if (this._shareId) {

				const _shareIdIdx = _filledPeerIds.findIndex((v) => v === this._shareId)
				if (_shareIdIdx > -1) {

					_filledPeerIds.splice(_shareIdIdx, 1);
				}
			}

			// 未使用 peerId 列表
			const _willFillPeerIds = _allPeerIds.filter((peerId) => !_filledPeerIds.includes(peerId));

			this._pollPeerIds = _willFillPeerIds;

			if (this._pollTimer) {

				clearInterval(this._pollTimer);
			}

			// 主席模式下不需要执行自轮询
			if (this._store.getState().room.forceMode) {

				//_layoutConfig = await this.handlePoll(_layoutConfig);

				logger.debug('主席模式 _layoutConfig返回值 ', _layoutConfig);

			} else {

				// 只有队列中的元素个数大于窗口数时才执行轮询
				const _pollCount = _layoutConfig.data.filter((item) => item.type === 'poll').length;

				logger.debug('轮询窗口数量 [%s] 队列长度 [%s] 队列 %s', _pollCount, this._pollPeerIds.length, JSON.stringify(this._pollPeerIds));
				if (_pollCount < this._pollPeerIds.length) {
					this._pollTimer = setInterval(async () => {

						_layoutConfig = await this.handlePoll(_layoutConfig, [], true);

						logger.debug('_layoutConfig返回值 周期轮询', _layoutConfig);

					}, this._interval); // 定时轮询数据
				}
				// 立即执行轮询服务
				_layoutConfig = await this.handlePoll(_layoutConfig, _prevPollPeerIds);
				logger.debug('_layoutConfig返回值 轮询执行一次', _layoutConfig);
			}
		} else {

			clearInterval(this._pollTimer);
			logger.debug('轮询服务(暂停)队列: %o', this._pollPeerIds);
		}

		logger.debug(' spotlights 中 layoutConfig 更新: [前: "%s" 后: "%s"]', JSON.stringify(this._layoutConfig), JSON.stringify(_layoutConfig));

		let _isEqual = false;

		if (!this._simpleObjEqual(this._layoutConfig, _layoutConfig) || force) {

			logger.debug('_layoutConfigUpdated() | spotlights updated, emitting [layoutConfig: "%o"]', _layoutConfig);

			// 比较 _layoutConfig.data.peerId 是否相等
			const _prevPeerIds = this._layoutConfig.data.filter((v) => v.peerId).map((v) => (
				{
					id     : v.id,
					peerId : v.peerId,
					source : v.source,
					type   : v.type
				}));

			const _peerIds = _layoutConfig.data.filter((v) => v.peerId).map((v) => (
				{
					id     : v.id,
					peerId : v.peerId,
					source : v.source,
					type   : v.type
				}));

			_isEqual = this._arrEqual(_prevPeerIds, _peerIds, false);

			if (!this._arrEqual(_prevPeerIds, _peerIds) || force) {

				await this._roomClient.updateConsumers(_layoutConfig);  // 更新 layoutConfig 时更新
			}

			//this._store.dispatch(roomActions.setDisplayMode('autoSplit'));  // 更新 layoutConfig 后，需存一下当前的mode

			if (_filterVideo) {

				// 在过滤了无视频窗口后，如果没有了窗口，则用本地窗口填充(不管有没有视频)
				const _viewArea = _layoutConfig.data.find((v) => v.peerId);
				if (!_viewArea && _layoutConfig.data.length > 0) {

					Object.assign(_layoutConfig.data[0], {
						videoId     : null,
						local       : true,
						peerId      : this._localId,
						displayName : 'auto',
						source      : 'local',
						type        : 'auto'
					});
				}
			}

			if (source === 'handleLayoutChange' && _isEqual && this._layoutConfig.name === _layoutConfig.name) {

				logger.debug(`主席模式:autoSplit | spotlights not updated`);
			} else {

				logger.debug('更新 state 中 layoutConfig 来自 _layoutConfigUpdated 方法');

				// 处理语音激励窗口
				//this.swapSpeechViewArea(_layoutConfig);

				this._store.dispatch(roomActions.setLayoutConfig(_layoutConfig));
				this._layoutConfig = _layoutConfig;
			}

		} else {
			logger.debug('_layoutConfigUpdated() | spotlights not updated');

			if (_filterVideo) {

				const _viewArea = _layoutConfig.data.find((v) => v.peerId);
				if (!_viewArea && _layoutConfig.data.length > 0) {

					Object.assign(_layoutConfig.data[0], {

						videoId     : null,
						local       : true,
						peerId      : this._localId,
						displayName : 'auto',
						source      : 'local',
						type        : 'auto'
					});

					this._store.dispatch(roomActions.setLayoutConfig(_layoutConfig));
					this._layoutConfig = _layoutConfig;
				}
			}
		}

		const _endTime = Date.now();
		logger.debug('执行时长 _layoutConfigUpdated [ %s 毫秒]',(_endTime - _startTime));
	}

	/**
	 * 纠正视窗内容
	 */
	swapSpeechViewArea(layoutConfig) {

		// 语音激励窗口是否需要自动填充
		const _staticSpeechView = window.config.staticSpeechView;
		const _staticSpeechViewArea = layoutConfig.data.find((v) => v.type === 'speech');
		const _autoSpeechViewArea = layoutConfig.data.find((v) => v.source === 'active' && v.peerId);

		let _flag = false;

		if (_staticSpeechViewArea) {

			if (_autoSpeechViewArea && _autoSpeechViewArea.id !== _staticSpeechViewArea.id) {

				// 交换位置
				// _staticSpeechViewArea 与 _videoView 进行更换
				const _simpleSSVA = {
					videoId     : _staticSpeechViewArea.videoId,
					local       : _staticSpeechViewArea.local,
					peerId      : _staticSpeechViewArea.peerId,
					displayName : _staticSpeechViewArea.displayName,
					source      : _staticSpeechViewArea.source,
					videoMuted  : _staticSpeechViewArea.videoMuted
				};

				const _simpleASVA = {
					videoId     : _autoSpeechViewArea.videoId,
					local       : _autoSpeechViewArea.local,
					peerId      : _autoSpeechViewArea.peerId,
					displayName : _autoSpeechViewArea.displayName,
					source      : _autoSpeechViewArea.source,
					videoMuted  : _autoSpeechViewArea.videoMuted
				};

				Object.assign(_staticSpeechViewArea, _simpleASVA);
				Object.assign(_autoSpeechViewArea, _simpleSSVA);

				_flag = true;

			} else if (!_autoSpeechViewArea) {

				if (_staticSpeechView) {  // 独占窗口

				} else {  // 用一个普通视频填充
					const { filterVideo : _filterVideo } = this._store.getState().room;

					const _allPeerIds = this._allLitePeers.filter((v) => {
						if (_filterVideo) {

							return v.joinMode === 0 && !v.videoMuted
						}
						else {

							return v.joinMode === 0
						}
					}).map((v) => v.id);
					// 排序 _allPeerIds 已经基于状态值对视频窗口进行了过滤处理
					_allPeerIds.sort(sortCompare);

					// 已经填充的 peerId 列表
			        const _filledPeerIds = layoutConfig.data.filter((v) => v.peerId !== null && v.source !== 'share').map((v) => v.peerId);

					// 未使用 peerId 列表
					const _willFillPeerIds = _allPeerIds.filter((peerId) => !_filledPeerIds.includes(peerId));

					if (_willFillPeerIds.length > 0) {

						const _peerId = _willFillPeerIds[0];

						_staticSpeechViewArea.videoId = null;
						_staticSpeechViewArea.displayName = 'auto';

						_staticSpeechViewArea.local = _peerId === this._localId ? true : false;
						_staticSpeechViewArea.peerId = _peerId;
						_staticSpeechViewArea.source = _peerId === this._localId ? 'local' : 'remote';

						_flag = true;
					}
				}
			}
		}

		return _flag;
	}
}
